import React, { createContext, useContext, useState, useCallback, useEffect, ReactNode } from 'react';
import { ErrorModal } from '../components/ErrorModal';
import { registerErrorHandler } from '../utils/errorManager';

interface ErrorContextType {
  showError: (message: string, title?: string) => void;
  hideError: () => void;
}

const ErrorContext = createContext<ErrorContextType | undefined>(undefined);

/**
 * 全局错误上下文 Provider
 * 提供全局错误弹窗功能，并注册到全局错误管理器
 */
export const ErrorProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [visible, setVisible] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [errorTitle, setErrorTitle] = useState<string>('错误');

  const showError = useCallback((message: string, title: string = '错误') => {
    try {
      // 确保消息是字符串
      const safeMessage = typeof message === 'string' ? message : String(message);
      const safeTitle = typeof title === 'string' ? title : '错误';
      
      setErrorMessage(safeMessage);
      setErrorTitle(safeTitle);
      setVisible(true);
    } catch (err) {
      // 如果设置状态失败，fallback 到 alert
      console.error('[ErrorContext] Failed to show error:', err);
      alert(`${title}: ${message}`);
    }
  }, []);

  const hideError = useCallback(() => {
    setVisible(false);
    // 延迟清空消息，避免关闭动画时内容消失
    setTimeout(() => {
      setErrorMessage('');
      setErrorTitle('错误');
    }, 200);
  }, []);

  // 注册全局错误处理器，让 API 层面可以自动触发错误弹窗
  useEffect(() => {
    registerErrorHandler(showError);
  }, [showError]);

  return (
    <ErrorContext.Provider value={{ showError, hideError }}>
      {children}
      <ErrorModal
        visible={visible}
        title={errorTitle}
        message={errorMessage}
        onClose={hideError}
      />
    </ErrorContext.Provider>
  );
};

/**
 * 获取全局错误处理函数（可选，用于手动触发错误弹窗）
 */
export const useError = (): ErrorContextType => {
  const context = useContext(ErrorContext);
  if (!context) {
    throw new Error('useError must be used within ErrorProvider');
  }
  return context;
};

